import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, DestroyRef, effect, inject, untracked } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  forkJoin,
  fromEvent,
  map,
  of,
  skip,
  switchMap,
} from 'rxjs';

import { mapMenuItems } from '@demo/demo.menu';
import { KeyValueModel } from '@models/common';
import {
  CIPO_ADMIN_ROLE_ID,
  SYS_ROLE_FINANCIAL_ID,
  SYS_ROLE_HIGH_RISK_ID,
  SYS_ROLE_ROOT_ID,
  SYS_ROLE_SUPPORT_ID,
  User,
  UserSettings,
} from '@models/core';
import { MenuItem, RouteCodeEnum } from '@models/menu';
import { Permission } from '@models/permission';
import { TenantSettingsEnum, TenantSettingValue } from '@models/tenant';
import {
  A11Y_COLOR_FILTER,
  A11Y_CONTRAST,
  A11Y_DYSLEXIA_FONT,
  A11Y_FONT_SCALE,
  A11Y_LARGE_CURSOR,
  A11Y_LINE_HEIGHT,
  A11Y_READING_AID,
  A11Y_REDUCE_MOTION,
  A11Y_SATURATION,
  A11Y_TEXT_SPACING,
  A11Y_UNDERLINE_LINKS,
  CIPO_THEME,
  CIPO_THEME_MODE,
  DEBOUNCE_TIMES,
  LAST_CONTRACT_CONTEXT_STORAGE_KEY,
  MENU_COLLAPSED_KEY,
  MODULE,
} from '@shared/consts';
import { SignalRService, UserApiService } from '@shared/services';
import { ContextInfo, ContextInfoPart } from '@system/context/context.model';
import { UserSettingsService } from '@system/user/user.settings.service';

import { RoutesDictionaryByCode } from '@legacy/constants';
import { DataListType } from '@models/module/fields/main';
import { MainService } from '@module-instance/services';
import { ContextStore } from '@signal-store/context.store';
import { TenantStore } from '@signal-store/tenant.store';
import { BREAKPOINTS } from 'src/app-breakpoints.module';
import { HubHandlerSourceAngular } from '../models/system';
import { initialUserState as initialState, UserStoreActions, UserWithLastContractContext } from './models';
import {
  A11yColorFilter,
  A11yFontScale,
  A11yLineHeight,
  A11yPreferences,
  A11yReadingAid,
  A11ySaturation,
  A11yTextSpacing,
  applyAppearanceToBody,
  CipoTheme,
  CipoThemeMode,
  findMenuItemByCode,
  getMenuItems,
  Theme,
} from './services/helpers.store';
import { preserveLastContractContext, shouldKeepHierarchyContext } from './services/user-context.helpers';

const cloneMenuItems = (menuItems: MenuItem[]): MenuItem[] =>
  menuItems.map(menuItem => ({
    ...menuItem,
    children: menuItem.children ? cloneMenuItems(menuItem.children) : menuItem.children,
  }));

export const UserStore = signalStore(
  { providedIn: 'root' },
  withDevtools('user'),
  withState(initialState),

  withComputed(
    ({ userData, settings, menu }, tenantStore = inject(TenantStore), contextStore = inject(ContextStore)) => {
      const getTenantSettingValue = (id: TenantSettingsEnum): TenantSettingValue | undefined => {
        const tenantSetting = tenantStore.settings().find(s => s.id === id);
        return tenantSetting?.calculatedValue ?? tenantSetting?.value;
      };

      const calculatedUserSettings = (): UserSettings => {
        const dateFormat = getTenantSettingValue(TenantSettingsEnum.date_format);
        const timeFormat = getTenantSettingValue(TenantSettingsEnum.time_format);
        const decimalSeparator = getTenantSettingValue(TenantSettingsEnum.decimal_separator);
        const thousandSeparator = getTenantSettingValue(TenantSettingsEnum.thousand_separator);
        const loadMore = settings()?.loadMore ?? getTenantSettingValue(TenantSettingsEnum.load_more);
        const showLastVisitedModule =
          settings()?.showLastVisitedModule ?? getTenantSettingValue(TenantSettingsEnum.show_last_visited_module);

        return {
          dateFormat,
          timeFormat,
          datetimeFormat: dateFormat && timeFormat ? `${dateFormat} ${timeFormat}` : undefined,
          decimalSeparator,
          thousandSeparator,
          loadMore,
          showLastVisitedModule,
        } as UserSettings;
      };

      const contextAllSelected = computed(
        () =>
          userData()?.context !== undefined &&
          (userData()?.context?.program ?? userData()?.context?.project ?? userData()?.context?.contract) === undefined,
      );

      const hasSysRole = (roleId: number) => (userData()?.sysRoles ?? []).some(role => role.key === roleId);

      const isSysAdminRoot = computed(() => hasSysRole(SYS_ROLE_ROOT_ID));

      return {
        tenantId: computed(() => userData()?.tenantId ?? 0),
        program: computed(() => userData()?.context?.program),
        project: computed(() => userData()?.context?.project),
        contract: computed(() => userData()?.context?.contract),
        contextEntityInstanceId: computed(
          () =>
            userData()?.context?.contract?.entityInstanceId ??
            userData()?.context?.project?.entityInstanceId ??
            userData()?.context?.program?.entityInstanceId,
        ),
        contextAllSelected,
        multipleContractsSelected: computed(
          () =>
            // all contracts selected
            contextAllSelected() ||
            // program or project selected
            ((userData()?.context?.program?.id ?? userData()?.context?.project?.id) > 0 &&
              !userData()?.context?.contract?.id),
        ),
        currency: computed(() => {
          const currencyId =
            userData()?.context?.contract?.currencyId ??
            userData()?.context?.project?.currencyId ??
            userData()?.context?.program?.currencyId ??
            userData()?.defaultCurrencyId;
          return contextStore.getCurrency(currencyId);
        }),
        menuItemsList: computed(() => getMenuItems(menu.items() ?? [])),
        calculatedUserSettings: computed(() => calculatedUserSettings()),
        rolesName: computed(() => (userData()?.roles ?? []).map(role => role.value)),
        rolesNameOnCurrentContract: computed(() =>
          (userData()?.rolesOnCurrentContract ?? []).map(role => role.roleName),
        ),
        isCipoAdminGlobal: computed(() => (userData()?.roles ?? []).some(role => role.key === CIPO_ADMIN_ROLE_ID)),
        isCipoAdminOnCurrentContract: computed(() =>
          (userData()?.rolesOnCurrentContract ?? []).some(role => role.id === CIPO_ADMIN_ROLE_ID),
        ),
        isCipoAdminOnCurrentContractOrGlobal: computed(
          () =>
            (userData()?.rolesOnCurrentContract ?? []).some(role => role.id === CIPO_ADMIN_ROLE_ID) ||
            (userData()?.roles ?? []).some(role => role.key === CIPO_ADMIN_ROLE_ID),
        ),
        isSysAdmin: computed(() => (userData()?.sysRoles ?? []).length > 0),
        isSysAdminRoot: isSysAdminRoot,
        isSysAdminSupport: computed(() => isSysAdminRoot() || hasSysRole(SYS_ROLE_SUPPORT_ID)),
        isSysAdminFinancial: computed(() => isSysAdminRoot() || hasSysRole(SYS_ROLE_FINANCIAL_ID)),
        isSysAdminHighRisk: computed(() => isSysAdminRoot() || hasSysRole(SYS_ROLE_HIGH_RISK_ID)),
      };
    },
  ),

  withMethods(
    (
      store,
      tenantStore = inject(TenantStore),
      userService = inject(UserApiService),
      userSettingsService = inject(UserSettingsService),
      mainService = inject(MainService),
      signalRService = inject(SignalRService),
      router = inject(Router),
    ) => {
      const getLastContractContextStorageKey = (userData: UserWithLastContractContext) =>
        `${LAST_CONTRACT_CONTEXT_STORAGE_KEY}.${userData.tenantId}.${userData.id}`;

      const persistLastContractContext = (userData: UserWithLastContractContext, context?: ContextInfo) => {
        const entityInstanceId = context?.contract?.entityInstanceId;
        if (entityInstanceId) {
          sessionStorage.setItem(getLastContractContextStorageKey(userData), entityInstanceId.toString());
        }
      };

      const restoreLastContractContext = (userData: UserWithLastContractContext) => {
        const userDataWithLastContractContext = preserveLastContractContext(userData);
        if (userDataWithLastContractContext.lastContractContext?.contract) {
          persistLastContractContext(
            userDataWithLastContractContext,
            userDataWithLastContractContext.lastContractContext,
          );
          return of(userDataWithLastContractContext);
        }

        const storageKey = getLastContractContextStorageKey(userDataWithLastContractContext);
        const entityInstanceId = Number(sessionStorage.getItem(storageKey));
        if (!entityInstanceId) {
          return of(userDataWithLastContractContext);
        }

        return userService.getContextByEntityInstanceId(entityInstanceId).pipe(
          map(context => {
            if (!context?.contract) {
              sessionStorage.removeItem(storageKey);
            }
            return {
              ...userDataWithLastContractContext,
              lastContractContext: context?.contract ? context : undefined,
            };
          }),
          catchError(() => {
            sessionStorage.removeItem(storageKey);
            return of(userDataWithLastContractContext);
          }),
        );
      };

      const setActiveMenuItem = (activeMenuItem: MenuItem, updateContext: boolean = true) =>
        updateState(store, UserStoreActions.SetActiveMenuItem, ({ menu, userData }) => {
          if (!activeMenuItem.moduleId) {
            sessionStorage.removeItem(MODULE);
          } else {
            sessionStorage.setItem(MODULE, activeMenuItem.moduleId?.toString());
          }
          if (!updateContext) {
            return {
              menu: { ...menu, activeMenuItem },
            };
          }
          const newContext = shouldKeepHierarchyContext(activeMenuItem, userData.context)
            ? userData.context
            : (userData.lastContractContext ?? userData.context);
          return {
            menu: { ...menu, activeMenuItem },
            userData: {
              ...userData,
              context: newContext,
              lastContractContext: newContext?.contract ? newContext : userData.lastContractContext,
            },
          };
        });

      const setActiveMenuByModuleId = (moduleId: number, updateContext: boolean = true) => {
        const activeMenuItem = store.menuItemsList().find(m => !m.hidden && m.moduleId === moduleId);
        if (activeMenuItem) {
          setActiveMenuItem(activeMenuItem, updateContext);
        }
        return activeMenuItem;
      };

      const setIsLoading = (isLoading: boolean) => updateState(store, UserStoreActions.ToggleLoading, { isLoading });

      const reloadMenu = (onMenuLoaded?: () => void) =>
        userService.getUserMenu(store.contract()?.id, store.contextEntityInstanceId()).subscribe(items => {
          updateState(store, UserStoreActions.SetMenuItems, ({ menu }) => ({ menu: { ...menu, items } }));
          onMenuLoaded?.();
        });

      const setReloadContext = (reloadContext: boolean) =>
        updateState(store, UserStoreActions.SetReloadContext, { reloadContext });

      const refreshUserAccess = () => {
        const contractId = store.contract()?.id;
        const entityInstanceId = store.contextEntityInstanceId();

        forkJoin({
          userData: userService.getUserData(),
          menu: userService.getUserMenu(contractId, entityInstanceId),
          contractRoles: contractId ? mainService.getContractRoles(contractId) : of([]),
        }).subscribe(({ userData, menu, contractRoles }) => {
          const refreshedUserData = preserveLastContractContext(
            {
              ...userData,
              rolesOnCurrentContract: contractRoles,
            },
            store.userData()?.lastContractContext,
          );

          updateState(store, UserStoreActions.SetUserDataAndMenu, state => ({
            menu: { ...state.menu, items: menu },
            userData: refreshedUserData,
          }));
          setUserHubConnection(refreshedUserData);
          setReloadContext(true);
        });
      };

      const setUser = (onUserLoaded?: () => void, keepLoadingState: boolean = false) => {
        updateState(store, UserStoreActions.LoadingUserData, ({ menu }) => ({
          isLoading: true,
          userData: null,
          unreadNotifications: 0,
          menu: { ...initialState.menu, isCollapsed: menu.isCollapsed },
        }));
        updateState(store, UserStoreActions.EnableDemo, { devMode: false });
        userSettingsService.getUserSettings().subscribe(rawSettings => {
          updateState(store, UserStoreActions.SetSettings, { settings: getCalculatedValues(rawSettings) });
        });
        userService
          .getUserData()
          .pipe(switchMap(userData => restoreLastContractContext(userData)))
          .subscribe(userDataWithLastContractContext => {
            updateState(store, UserStoreActions.SetUserData, { userData: userDataWithLastContractContext });
            setUserHubConnection(userDataWithLastContractContext);
            reloadUserData(false);
            reloadMenu(() => {
              updateState(store, UserStoreActions.SetUserData, { isLoading: keepLoadingState });
              onUserLoaded?.();
            });
            readAndSetUserRolesOnCurrentContract(store.contract()?.id);
          });
      };

      const getContractIdFromQueryUrl = () => {
        // this will be removed after implementing these pages as angular components
        // or could be modified to read contract id from the route
        // it searched the contractId in the query url: i.e. tenant-abbr/emails?e=10120&f=-1&contractId=52
        const searchParams = new URLSearchParams(window.location.search);
        const contractIdKey = [...searchParams.keys()].find(key => key.toLowerCase() === 'contractid');
        return contractIdKey ? searchParams.get(contractIdKey) : undefined;
      };

      const getContractIdFromRoute = () => {
        // this will be removed after implementing these pages as angular components
        // or could be modified to read contract id from the route
        const route = router.routerState.snapshot.url || window.location.pathname;
        if (
          route.includes('/dm/') ||
          route.includes('/sov/') ||
          route.includes('/fn/') ||
          route.includes('/progresspayment/') ||
          route.includes('/management-reports/') ||
          route.includes('/emails/') ||
          route.includes('/pdf-markup/')
        ) {
          return Number(route.split('/')[3]);
        }
        const contractIdFromQueryUrl = getContractIdFromQueryUrl();
        if (contractIdFromQueryUrl) {
          return Number(contractIdFromQueryUrl);
        }
        return undefined;
      };

      const getEntityInstanceIdFromRoute = () => {
        const route = router.routerState.snapshot.url || window.location.pathname;
        const entityInstanceId = /\/file-contract-manager\/(\d+)/.exec(route)?.[1];
        return entityInstanceId ? Number(entityInstanceId) : undefined;
      };

      const getContractFromStoreAndRoute = (isContextChanged: boolean) => {
        let contractId = store.contract()?.id;
        let entityInstanceId = store.contextEntityInstanceId();
        if (isContextChanged) {
          return of({ contractId, entityInstanceId });
        }

        const contractIdFromRoute = getContractIdFromRoute();
        const entityInstanceIdFromRoute = getEntityInstanceIdFromRoute();

        if (store.tenantId() && contractIdFromRoute) {
          // verify if the contract id from the route is different than the one stored in the store
          if (contractIdFromRoute != contractId) {
            contractId = contractIdFromRoute;
            const contextFromRoute = { contract: { id: contractId } } as ContextInfo;

            // A deep link is an explicit context selection. Keep it as the last contract as
            // well, otherwise menu activation can restore the context from the previous tab.
            updateState(store, UserStoreActions.SetContractId, ({ userData }) => ({
              userData: {
                ...userData,
                context: contextFromRoute,
                lastContractContext: contextFromRoute,
              },
            }));
          }
        } else if (store.tenantId() && entityInstanceIdFromRoute) {
          // verify if the contract id from the route is different than the one stored in the store
          if (entityInstanceIdFromRoute != contractId) {
            entityInstanceId = entityInstanceIdFromRoute;

            return userService.getContextByEntityInstanceId(entityInstanceId).pipe(
              map(context => {
                if (context) {
                  if (context.contract?.id) {
                    contractId = context.contract.id;
                  }

                  // patch the context from the route and store it
                  updateState(store, UserStoreActions.SetUserContext, ({ userData }) => ({
                    userData: { ...userData, context },
                  }));
                }
                return { contractId, entityInstanceId };
              }),
            );
          }
        }
        return of({ contractId, entityInstanceId });
      };

      // all the requests needed after loading the user data
      const reloadUserData = (isContextChanged: boolean) => {
        updateState(store, UserStoreActions.LoadingUserData, state => ({
          userData: { ...state.userData, isLoading: true },
        }));
        getContractFromStoreAndRoute(isContextChanged).subscribe(({ contractId, entityInstanceId }) => {
          // load only if we have a contract id and a tenant id
          if (store.tenantId()) {
            contractId = isNaN(Number(contractId)) ? 0 : Number(contractId);
            forkJoin({
              menu: userService.getUserMenu(contractId, entityInstanceId),
              unreadNotifications: userService.getUnreadNotifications(contractId),
            }).subscribe(({ menu, unreadNotifications }) => {
              updateState(store, UserStoreActions.SetUserDataAndMenu, state => ({
                unreadNotifications,
                menu: { ...state.menu, items: menu },
                userData: { ...state.userData, isLoading: false },
              }));
            });
          } else {
            updateState(store, UserStoreActions.LoadingUserDataFailed, state => ({
              userData: { ...state.userData, isLoading: false },
            }));
          }
        });
      };

      const getCalculatedValues = (rawSettings: KeyValueModel<TenantSettingsEnum, string>[]): UserSettings => {
        const settings: UserSettings = {} as UserSettings;
        [...(rawSettings || [])].forEach(rawSetting => getCalculatedValue(rawSettings, settings, rawSetting.key));
        return settings;
      };

      const getCalculatedValue = (
        rawSettings: KeyValueModel<TenantSettingsEnum, string>[],
        settings: UserSettings,
        id: TenantSettingsEnum,
      ) => {
        switch (id) {
          case TenantSettingsEnum.load_more:
            settings.loadMore = getBool(rawSettings, TenantSettingsEnum.load_more);
            break;
        }
      };

      const getBool = (rawSettings: KeyValueModel<TenantSettingsEnum, string>[], id: TenantSettingsEnum): boolean => {
        const s = rawSettings
          .find(s => s.key === id)
          ?.value.toString()
          .toLowerCase();
        return ['true', 'yes', '1'].includes(s);
      };

      const readAndSetUserRolesOnCurrentContract = (contractId: number) => {
        mainService.getContractRoles(contractId).subscribe(contractRoles => {
          updateState(store, UserStoreActions.SetUserRolesOnCurrentContract, ({ userData }) => ({
            userData: { ...userData, rolesOnCurrentContract: contractRoles },
          }));
        });
      };

      const updateContext = (context: ContextInfo) => {
        if (context.contract) {
          // context per contract
          const previousContractId = store.contract()?.id;
          const thisContractId = Number(context.contract.id);
          if (previousContractId != null && Number(previousContractId) === thisContractId) {
            updateState(store, UserStoreActions.SetContextWithContract, ({ userData }) => ({
              userData: {
                ...userData,
                lastContractContext: userData.context?.contract ? userData.context : context,
              },
            }));
            persistLastContractContext(store.userData(), context);
            return;
          }
          updateState(store, UserStoreActions.SetContextWithContract, ({ userData }) => ({
            userData: { ...userData, context, lastContractContext: context, isLoading: true },
          }));
          persistLastContractContext(store.userData(), context);
          reloadUserData(true);

          // load the roles for the contract and update the user data with the roles
          readAndSetUserRolesOnCurrentContract(thisContractId);
        } else {
          // context not per contract
          const previousEntityInstanceId = store.contextEntityInstanceId();
          const newEntityInstanceId =
            context?.contract?.entityInstanceId ??
            context?.project?.entityInstanceId ??
            context?.program?.entityInstanceId;
          if (previousEntityInstanceId && previousEntityInstanceId === newEntityInstanceId) {
            // no change, no need to reload data
            return;
          }
          updateState(store, UserStoreActions.SetContextWithoutContract, ({ userData }) => ({
            userData: { ...userData, context, lastContractContext: userData.lastContractContext, isLoading: false },
          }));
          const menuItems = cloneMenuItems(store.menu().items);
          userService.setUserMenuUrlPath(
            menuItems,
            store.userData().lastContractContext?.contract?.id,
            newEntityInstanceId,
          );
          updateState(store, UserStoreActions.SetMenuItems, ({ menu }) => ({ menu: { ...menu, items: menuItems } }));
        }
      };

      const setUserHubConnection = (userData: User, context?: ContextInfo) => {
        signalRService.refresh({
          userId: userData.id,
          sysUserId: userData.sysUserId,
          tenantId: userData.tenantId,
          context: context ?? userData.context,
        });
      };

      return {
        setIsLoading,
        setUser,
        setUserAndGoToLandingPage: () => {
          // load the user data
          setUser(() => {
            // default landing page is dashboard
            let urlPath = `${tenantStore.selectedTenantName()}/${RoutesDictionaryByCode[RouteCodeEnum.Dashboard]}`;

            // check if we need to go to the last visited module
            const lastVisitedModuleId = store.userData()?.lastVisitedModuleId;
            const showLastVisitedModule = store.userData()?.showLastVisitedModule ?? false;
            if (showLastVisitedModule && lastVisitedModuleId !== undefined && lastVisitedModuleId !== undefined) {
              const menuItem = setActiveMenuByModuleId(lastVisitedModuleId, false);
              if (menuItem?.urlPath) {
                urlPath = menuItem.urlPath;
              }
            }

            // navigate to the landing page
            router.navigateByUrl(`/${urlPath}`).finally(() => {
              // in case of navigation success or failure, ensure user loading is turned off
              // this makes the loading spinner to disappear and the landing page to appear
              setIsLoading(false);
            });
          }, true);
        },
        refreshUserData: () =>
          userService.getUserData().subscribe(userData => {
            const refreshedUserData = preserveLastContractContext(userData, store.userData()?.lastContractContext);
            updateState(store, UserStoreActions.SetUserData, { userData: refreshedUserData });
            setUserHubConnection(refreshedUserData);
          }),
        refreshUserAccess,
        refreshUnreadNotifications: () =>
          userService
            .getUnreadNotifications(store.contract()?.id)
            .subscribe(unreadNotifications =>
              updateState(store, UserStoreActions.SetNotifications, { unreadNotifications }),
            ),
        updateUserData: (userData: UserWithLastContractContext) => {
          updateState(store, UserStoreActions.SetUserData, {
            userData: preserveLastContractContext(userData, store.userData()?.lastContractContext),
          });
        },
        updateUserLastVisitedDashboardModule: (moduleId: number) => {
          if (store.userData()?.dashboardLastVisitedModuleId === moduleId) {
            return;
          }
          userService.setLastVisitedDashboardModule(moduleId).subscribe({
            next: () => {
              updateState(store, UserStoreActions.SetUserData, ({ userData }) => ({
                userData: {
                  ...userData,
                  dashboardLastVisitedModuleId: moduleId,
                },
              }));
            },
            error: () => undefined,
          });
        },
        updateUserLastVisitedModule: (moduleId: number) => {
          if (store.userData()?.lastVisitedModuleId === moduleId) {
            return;
          }
          updateState(store, UserStoreActions.SetUserData, ({ userData }) => ({
            userData: {
              ...userData,
              lastVisitedModuleId: moduleId,
            },
          }));
          userService.setLastVisitedModule(moduleId).subscribe({ error: () => undefined });
        },

        setActiveMenuItem,
        setActiveMenuByModuleId,
        setActiveMenuByUrl: (updateContext: boolean = true) => {
          const activeMenuItem = store
            .menuItemsList()
            .filter(m => !m.hidden && !!m.moduleId)
            .find(item => userService.isMenuItemMatchingRoute(item));
          if (activeMenuItem) {
            const currentActiveMenuItem = store.menu.activeMenuItem();
            // A context change can keep the same module active while changing its contract-aware URL.
            if (
              activeMenuItem.moduleId === currentActiveMenuItem?.moduleId &&
              activeMenuItem.urlPath === currentActiveMenuItem.urlPath
            ) {
              return;
            }
            setActiveMenuItem(activeMenuItem, updateContext);
          }
        },
        setActiveMenuByCode: (code: RouteCodeEnum, updateContext: boolean = true) => {
          const activeMenuItem = store.menuItemsList().find(item => item.code === code);
          if (activeMenuItem) {
            setActiveMenuItem(activeMenuItem, updateContext);
          }
        },
        reloadMenu,
        collapseMenu: (force?: boolean) => {
          const isCollapsed = force !== undefined ? force : !store.menu.isCollapsed();
          if (isCollapsed) {
            localStorage.setItem(MENU_COLLAPSED_KEY, 'true');
          } else {
            localStorage.removeItem(MENU_COLLAPSED_KEY);
          }

          updateState(store, UserStoreActions.CollapseMenu, ({ menu }) => ({ menu: { ...menu, isCollapsed } }));
        },

        hasPermission: (permission: Permission) => {
          const activeMenuItem = store.menu.activeMenuItem();
          const menuItems = store.menuItemsList();
          const refreshedActiveMenuItem =
            menuItems.find(item => item.moduleId === activeMenuItem?.moduleId) ??
            menuItems.find(item => item.urlPath === activeMenuItem?.urlPath) ??
            activeMenuItem;

          return !!refreshedActiveMenuItem?.operations?.find(op => op.id === permission);
        },

        getModuleByCode: (code: RouteCodeEnum) => findMenuItemByCode(store.menuItemsList(), code),

        setReloadContext,
        //   this should be reviewed when refactoring context (context should always include all the available data)
        updateContext: (context: ContextInfo) => {
          updateContext(context);
          setUserHubConnection(store.userData(), context);
        },
        updateContextByContractId: (contractId: number | string) => {
          const id = Number(contractId);
          if (Number.isNaN(id)) {
            return;
          }
          const context: ContextInfo = { contract: { id } as ContextInfoPart };
          updateContext(context);
          setUserHubConnection(store.userData(), context);
        },

        setAngularJsRoute: (angularJsRoute: boolean) =>
          updateState(store, UserStoreActions.SetAngularJsRoute, { angularJsRoute }),
        setHideHeader: (hideHeader: boolean) => updateState(store, UserStoreActions.SetHideHeader, { hideHeader }),
        setUnreadNotifications: (unreadNotifications: number) =>
          updateState(store, UserStoreActions.SetNotifications, { unreadNotifications }),

        updateTheme: (newTheme: Partial<Theme>) => {
          const theme = { ...store.theme(), ...newTheme };
          updateState(store, UserStoreActions.setTheme, { theme });
          if (newTheme.color) {
            localStorage.setItem(CIPO_THEME, newTheme.color);
          }
          if (newTheme.mode) {
            localStorage.setItem(CIPO_THEME_MODE, newTheme.mode);
          }
          applyAppearanceToBody(theme, store.a11y());
        },

        updateA11y: (partial: Partial<A11yPreferences>) => {
          const a11y = { ...store.a11y(), ...partial };
          updateState(store, UserStoreActions.setA11y, { a11y });
          localStorage.setItem(A11Y_CONTRAST, a11y.contrast);
          localStorage.setItem(A11Y_REDUCE_MOTION, String(a11y.reduceMotion));
          localStorage.setItem(A11Y_FONT_SCALE, a11y.fontScale);
          localStorage.setItem(A11Y_TEXT_SPACING, a11y.textSpacing);
          localStorage.setItem(A11Y_LINE_HEIGHT, a11y.lineHeight);
          localStorage.setItem(A11Y_SATURATION, a11y.saturation);
          localStorage.setItem(A11Y_UNDERLINE_LINKS, String(a11y.underlineLinks));
          localStorage.setItem(A11Y_DYSLEXIA_FONT, String(a11y.dyslexiaFont));
          localStorage.setItem(A11Y_COLOR_FILTER, a11y.colorFilter);
          localStorage.setItem(A11Y_LARGE_CURSOR, String(a11y.largeCursor));
          localStorage.setItem(A11Y_READING_AID, a11y.readingAid);
          applyAppearanceToBody(store.theme(), a11y);
        },

        loadSettings: () =>
          userSettingsService
            .getUserSettings()
            .subscribe(rawSettings =>
              updateState(store, UserStoreActions.SetSettings, { settings: getCalculatedValues(rawSettings) }),
            ),
        getSettings: () => store.settings(),

        //   until angularJs is removed
        closeJsDialog: (dialogData: unknown) => updateState(store, UserStoreActions.CloseJsDialog, { dialogData }),
        setLanguage: (language: string) => updateState(store, UserStoreActions.setLanguage, { language }),

        setAssistantOpened: (assistantOpened: boolean) =>
          updateState(store, UserStoreActions.SetAssistantOpened, ({ cipoAI }) => ({
            cipoAI: { ...cipoAI, assistantOpened },
          })),
      };
    },
  ),

  withHooks(
    (
      store,
      tenantStore = inject(TenantStore),
      router = inject(Router),
      signalRService = inject(SignalRService),
      destroyRef = inject(DestroyRef),
    ) => ({
      onInit: () => {
        let routeSyncedMenuItem: MenuItem | undefined;

        const color = (localStorage.getItem(CIPO_THEME) as CipoTheme) ?? 'default';
        const mode = (localStorage.getItem(CIPO_THEME_MODE) as CipoThemeMode) ?? 'light';
        const storedContrast = localStorage.getItem(A11Y_CONTRAST);
        const theme: Theme = { color, mode: storedContrast === 'invert' && mode === 'light' ? 'dark' : mode };

        const a11y: A11yPreferences = {
          contrast: storedContrast === 'high' ? 'high' : 'normal',
          reduceMotion: localStorage.getItem(A11Y_REDUCE_MOTION) === 'true',
          fontScale: (localStorage.getItem(A11Y_FONT_SCALE) as A11yFontScale) ?? 'normal',
          textSpacing: (localStorage.getItem(A11Y_TEXT_SPACING) as A11yTextSpacing) ?? 'normal',
          lineHeight: (localStorage.getItem(A11Y_LINE_HEIGHT) as A11yLineHeight) ?? 'normal',
          saturation: (localStorage.getItem(A11Y_SATURATION) as A11ySaturation) ?? 'normal',
          underlineLinks: localStorage.getItem(A11Y_UNDERLINE_LINKS) === 'true',
          dyslexiaFont: localStorage.getItem(A11Y_DYSLEXIA_FONT) === 'true',
          colorFilter: (localStorage.getItem(A11Y_COLOR_FILTER) as A11yColorFilter) ?? 'normal',
          largeCursor: localStorage.getItem(A11Y_LARGE_CURSOR) === 'true',
          readingAid: (localStorage.getItem(A11Y_READING_AID) as A11yReadingAid) ?? 'off',
        };

        // Migrate the retired Invert preference without overriding an explicit Browser choice.
        if (storedContrast === 'invert') {
          localStorage.setItem(A11Y_CONTRAST, a11y.contrast);
          localStorage.setItem(CIPO_THEME_MODE, theme.mode);
        }
        updateState(store, UserStoreActions.setTheme, { theme, a11y });
        applyAppearanceToBody(theme, a11y);

        const colorScheme = window.matchMedia?.('(prefers-color-scheme: dark)');
        if (colorScheme) {
          fromEvent(colorScheme, 'change')
            .pipe(takeUntilDestroyed(destroyRef))
            .subscribe(() => {
              if (store.theme().mode === 'browser') {
                store.updateTheme({ mode: 'browser' });
              }
            });
        }

        const menuEffect = effect(
          () => {
            const menuItems = store.menu.items();
            if (!!menuItems.length && localStorage.getItem(MENU_COLLAPSED_KEY) === 'true') {
              store.collapseMenu();
              menuEffect.destroy();
            }
          },
          { manualCleanup: true },
        );

        router.events
          .pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntilDestroyed(),
          )
          .subscribe(() => {
            store.setActiveMenuByUrl(false);
            // Route synchronization must not navigate back to a context-dependent menu URL.
            routeSyncedMenuItem = store.menu.activeMenuItem();
          });

        effect(() => {
          // set active menu item based on the current url
          if (store.menu.items()?.length) {
            untracked(() => store.setActiveMenuByUrl());
          }
        });

        effect(() => {
          const menuItems = store.menu.items();
          if (!menuItems.length) {
            return;
          }

          if (!tenantStore.isDemoTenant()) {
            return;
          }

          if (!menuItems.find(item => item.id === -999)) {
            const demoMenu: MenuItem = {
              id: -999,
              name: 'Demo',
              children: mapMenuItems(store.userData()?.tenantIdentifier?.toLowerCase() ?? ''),
              defaultDataListType: DataListType.Module,
            };
            menuItems.push(demoMenu);
            updateState(store, UserStoreActions.EnableDemo, ({ menu }) => ({
              menu: { ...menu, items: menuItems },
              devMode: true,
            }));
          }
        });

        //   this should be moved in context-view after refactoring the context
        //   TODO: Re-check this - it triggers the navigation after menuItemClicked (although it's skupped by the router)
        toObservable(store.menu.activeMenuItem)
          .pipe(
            filter(item => !!item),
            //   to avoid the first emission, that can remove the url parts (as /details/...)
            skip(1),
            distinctUntilKeyChanged('urlPath'),
          )
          .subscribe(activeMenuItem => {
            if (router.currentNavigation() || activeMenuItem === routeSyncedMenuItem) {
              return;
            }
            const { urlPath } = activeMenuItem;
            if (urlPath && !window.location.pathname.includes(urlPath.split('?')[0])) {
              router.navigateByUrl(`/${urlPath}`, { replaceUrl: true });
            }
          });

        /* This effect makes sure that the dialogData gets cleared after a while.
        We need this because if you close a dialog => open it again => close again, the dialogData would be the same
        and the effects will not run (they don't run if the new value is the same) */
        effect(() => {
          const dialogData = store.dialogData();
          if (![null, undefined].includes(dialogData)) {
            setTimeout(() => {
              untracked(() => store.closeJsDialog(null));
            }, 10);
          }
        });

        /* This effect updates the user's last visited module when the active menu item changes */
        effect(() => {
          const moduleId = store.menu.activeMenuItem()?.moduleId;
          const lastVisitedModuleId = untracked(() => store.userData()?.lastVisitedModuleId);
          if (moduleId && moduleId !== lastVisitedModuleId) {
            untracked(() => store.updateUserLastVisitedModule(moduleId));
          }
        });

        // Keep isSmallScreen reactive — update whenever the viewport crosses the breakpoint
        const isSmallScreen = window.innerWidth < BREAKPOINTS.md;
        if (isSmallScreen) {
          updateState(store, UserStoreActions.setSmallScreen, { isSmallScreen: true });
        }
        fromEvent(window, 'resize')
          .pipe(
            debounceTime(DEBOUNCE_TIMES.short),
            map(() => window.innerWidth < BREAKPOINTS.md),
            distinctUntilChanged(),
          )
          .subscribe(isSmallScreen => updateState(store, UserStoreActions.setSmallScreen, { isSmallScreen }));

        // sample how to register handlers for SignalR events on Angular
        signalRService.registerHandler(HubHandlerSourceAngular, 'NewEmailSent', function (contractId) {
          console.log(HubHandlerSourceAngular, 'NewEmailSent to contractId', contractId);
        });
      },
    }),
  ),
);
